import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import IntlMessages from '@iso/components/utility/intlMessages';
import { Form, Input, Button, Select, Row, Col, DatePicker, Tabs, Card, Timeline, Popconfirm, InputNumber, Checkbox, TimePicker, Radio, Upload, Space } from 'antd';
import { activityPolicy } from '@iso/constants/activityPolicy';
import {
  safetyActivityHazardCategories,
  getSafetyActivityHazardsForCategory,
  getSafetyActivityHazardCategoryByHazard,
} from '@iso/constants/hazardTypes';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { propertyService, healthsafety, attachment, assetService } from '@iso/services';
import _ from 'lodash';
import L from 'leaflet';  
import { useSelector, useDispatch } from 'react-redux';
import { dateHelper } from '@iso/lib/helpers/dateHelper';
import moment from 'moment';
import { Map, TileLayer, FeatureGroup, Polygon, Tooltip, ZoomControl, Marker, Popup } from 'react-leaflet';
import { apiEndpoint, apiEndpointFunction } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import TagsInput from 'react-tagsinput'
import 'react-tagsinput/react-tagsinput.css'

const { Option } = Select;

const Activity = (props) => {

  const [form] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef();
  const [valueLat, setValueLat] = useState();
  const [valueLng, setValueLng] = useState();
  const activePropertyId = useSelector((state) => state.property.activePropertyId);

  const [attachmentActivityId, setAttachmentActivityId] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [showHazardOther, setShowHazardOther] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const hazardCategoryValue = Form.useWatch(['asset', 'hazard_category'], form);
  const hazardTypeValue = Form.useWatch(['asset', 'hazard_type'], form);
  const hazardOptions = useMemo(
    () => getSafetyActivityHazardsForCategory(hazardCategoryValue),
    [hazardCategoryValue]
  );
  const activityActive = props.activityActive;

  // Fetch users for the property to populate attendees dropdown
  useEffect(() => {
    if (!props.propertyId) {
      setUserOptions([]);
      return;
    }

    propertyService.getListUsersOfProperty(props.propertyId, true)
      .then((res) => {
        const users = Array.isArray(res?.users)
          ? res.users
          : Array.isArray(res?.data?.users)
            ? res.data.users
            : [];

        const formattedUsers = users.map((user) => {
          const displayName = user?.name || user?.email;
          if (!displayName) {
            return null;
          }
          if (user?.name && user?.email && user.name !== user.email) {
            return `${user.name} (${user.email})`;
          }
          return displayName;
        }).filter(Boolean);

        setUserOptions(formattedUsers);
      })
      .catch((error) => {
        console.error('Failed to load users for property', error);
      });
  }, [props.propertyId]);

  useEffect(() => {
    setFields([
      {
        name: ['asset', 'date_time'],
        value: moment(),
      },
      {
        name: ['asset', 'type'],
        value: activityActive,
      }   
    ]);
  }, [props.propertyId, activityActive]);

  const handleChangeTag = (input_tags) => {
    setAttendees(input_tags);
  }

  const handleClickMap = (e) => {
    const { lat, lng } = e.latlng;
    setValueLng(lng);
    setValueLat(lat);
  };

  const uploadPolicyProps = {
    name: "Attachment[file]",
    action: apiEndpoint.attachments,
    headers: authHeader(true),
    listType: "picture",
    multiple: true,
    onChange: (response) => {
      if (response.file && response.file.status == "done") {
        setAttachmentActivityId(attachmentActivityId => [...attachmentActivityId, response.file.response.attachment.id]);
      }
    },
    onRemove: file => {
      attachment.destroy(file.uid);
    }
  };

  const getLatLng = (cLat, cLng) => {
    const lat = cLat;
    const lng = cLng;
    return new L.LatLng(lat, lng);
  }

  const renderActivityPolicy = () => {
    let options = [];
    _.forEach(activityPolicy, (activity, index) => {
      // Update label for safety_walk
      let label = activity.label;
      if (activity.value === 'safety_walk') {
        label = 'Safety Activity - Safety Walk';
      }
      
      options.push(
        <Option key={index} value={activity.value}>
          <div>{label}</div>
        </Option>
      );
    })
    return (
      <Select
        placeholder="Select a activity"
        allowClear
        size="large"
      >
        {options}
      </Select>
    );
  }

  useEffect(() => {
    if (!hazardCategoryValue) {
      form.resetFields([['asset', 'hazard_type'], ['asset', 'hazard_type_other']]);
      return;
    }

    const currentHazardType = form.getFieldValue(['asset', 'hazard_type']);
    if (!currentHazardType) {
      return;
    }

    const derivedCategory = getSafetyActivityHazardCategoryByHazard(currentHazardType);
    if (derivedCategory && derivedCategory !== hazardCategoryValue) {
      form.resetFields([['asset', 'hazard_type'], ['asset', 'hazard_type_other']]);
    }
  }, [hazardCategoryValue, form]);

  useEffect(() => {
    if (!hazardTypeValue) {
      return;
    }

    const derivedCategory = getSafetyActivityHazardCategoryByHazard(hazardTypeValue);
    if (derivedCategory && derivedCategory !== hazardCategoryValue) {
      form.setFieldsValue({
        asset: {
          hazard_category: derivedCategory,
        },
      });
    }
  }, [hazardTypeValue, hazardCategoryValue, form]);

  useEffect(() => {
    if (hazardTypeValue === 'other') {
      setShowHazardOther(true);
    } else {
      if (showHazardOther) {
        form.resetFields([['asset', 'hazard_type_other']]);
      }
      setShowHazardOther(false);
    }
  }, [hazardTypeValue, form, showHazardOther]);

  const handleHazardCategoryChange = (value) => {
    if (!value) {
      form.resetFields([['asset', 'hazard_type'], ['asset', 'hazard_type_other']]);
      return;
    }
    form.resetFields([['asset', 'hazard_type'], ['asset', 'hazard_type_other']]);
  };

  const handleHazardSpecificChange = (value) => {
    if (value !== 'other') {
      form.resetFields([['asset', 'hazard_type_other']]);
    }
  };

  const renderOptionsAssets = (assets) => {
    let options = [];
    _.forEach(assets, (asset, index) => {
      options.push(
        <Option key={index} value={asset.id}>
          <div>{asset.title}</div>
        </Option>
      ); 
    })
    return (
      <Select
        placeholder="Select Assets"
        allowClear
        size="large"
      >
        {options}
      </Select>
    );
  }

  const handleSubmit = (e) => {
    setLoading(true);

    form
      .validateFields()
      .then(values => {
        onStore(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
        setLoading(false);
      });
  }

  const onStore = (values) => {
    var point = null;
   
    if (valueLat !== '' && valueLng !== '') {
      point = getLatLng(valueLat, valueLng);
    }
    
    const dataPolicy = {
      policy: {
        ...values.asset,
        idFacilities: values.asset.asset_id,
        property_id: props.propertyId ?? null,
        type: values.asset.type,
        date_time: values.asset.date_time ? dateHelper.dateTimeForAPI(values.asset.date_time) : '',
        point: point,
        attendees: attendees,
        attachment_activity_id: attachmentActivityId,
      },
    };

    healthsafety.storeOrUpdateActivityPolicies(dataPolicy).then(res => {
      if (res.code === '0000') {
        setLoading(false);
        form.resetFields();
        setAttendees([]);
        props.onSaved();
        setValueLat();
        setValueLng();
        setAttachmentActivityId([])
      } else {
        setLoading(false);
      }
    });
  }

  return (
    <Form
      form={form}
      layout="vertical"
      scrollToFirstError
      fields={fields}
      id="#1"
    >
      <div className="form-body">
        <Row>
          <Col xs={{ span: 24 }} md={{ span: 24 }}>
            <Form.Item
              name={['asset', 'type']}
              className="isoInputWrapper"
              label={<IntlMessages id="propertyPage.modal.asset.activityPolicy" />}
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
            >
              {renderActivityPolicy()}
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col xs={{ span: 24 }} md={{ span: 24 }}>
            <Form.Item
              name={['asset', 'asset_id']}
              className="isoInputWrapper"
              label={<IntlMessages id="healthSafety.history.assetName" />}
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
            >
              {renderOptionsAssets(props.assets)}
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col xs={{ span: 24 }} md={{ span: 24 }}>
            <Form.Item
              name={['asset', 'date_time']}
              className="isoInputWrapper"
              label={<IntlMessages id="healthSafety.history.dateTime" />}
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
            >
              <DatePicker format="DD-MM-YYYY HH:mm:ss" size="large" style={{ width: '100%' }} showTime />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => {
            return (prevValues['asset']);
          }}
        >
          {({ getFieldValue }) => {
            if (getFieldValue('asset')) {
              return (
                <div>
                  {getFieldValue('asset').type == 'hazard' && (
                    <>
                      <Row>
                        <Col xs={{ span: 24 }} md={{ span: 24 }}>
                          <Form.Item
                            name={['asset', 'hazard_category']}
                            className="isoInputWrapper"
                            label={<IntlMessages id="healthSafety.history.hazardCategory" />}
                            rules={[
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]}
                          >
                            <Select
                              placeholder="Select a hazard category"
                              allowClear
                              onChange={handleHazardCategoryChange}
                              size="large"
                            >
                              {safetyActivityHazardCategories.map((category) => (
                                <Option key={category.value} value={category.value}>
                                  <div>{category.label}</div>
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={{ span: 24 }} md={{ span: 24 }}>
                          <Form.Item
                            name={['asset', 'hazard_type']}
                            className="isoInputWrapper"
                            label={<IntlMessages id="healthSafety.history.hazardDetail" />}
                            rules={[
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]}
                          >
                            <Select
                              placeholder={hazardCategoryValue ? "Select a hazard" : "Select a hazard category first"}
                              allowClear
                              onChange={handleHazardSpecificChange}
                              size="large"
                              disabled={!hazardCategoryValue}
                              showSearch
                              optionFilterProp="children"
                            >
                              {hazardOptions.map((hazard) => (
                                <Option key={hazard.value} value={hazard.value}>
                                  <div>{hazard.label}</div>
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                  {showHazardOther &&
                    <Row>
                      <Col xs={{ span: 24 }} md={{ span: 24 }}>
                        <Form.Item
                          name={['asset', 'hazard_type_other']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="healthSafety.history.hazardTypeOther" />}
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <Input size="large" placeholder="Hazard type" />
                        </Form.Item>
                      </Col>
                    </Row>
                  }
                  {getFieldValue('asset').type === 'meeting' &&
                    <div>
                      <Row>
                        <Col xs={{ span: 24 }} md={{ span: 24 }}>
                          <Form.Item
                            name={['asset', 'meeting_name']}
                            className="isoInputWrapper"
                            label={<IntlMessages id="healthSafety.history.meetingName" />}
                            rules={[
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]}
                          >
                            <Input size="large" placeholder="Meeting Name" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  }
                  {(getFieldValue('asset').type == 'safety_walk' ||
                    getFieldValue('asset').type == 'hazard' ||
                    getFieldValue('asset').type == 'meeting' ||
                    getFieldValue('asset').type == 'induction' ||
                    getFieldValue('asset').type === 'toolbox_meeting'
                  ) &&
                    <Row>
                      <Col xs={{ span: 24 }} md={{ span: 24 }}>
                        <Form.Item
                          name={['asset', 'actions']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="healthSafety.history.actions" />}
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <Input.TextArea />
                        </Form.Item>
                      </Col>
                    </Row>
                  }
                  {getFieldValue('asset').type == 'induction' &&
                    <Row>
                      <Col xs={{ span: 24 }} md={{ span: 24 }}>
                        <Form.Item
                          name={['asset', 'person_running_induction']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="healthSafety.history.personRunningInduction" />}
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <Input size="large" placeholder="Person running Induction" />
                        </Form.Item>
                      </Col>
                    </Row>
                  }
                  {(
                    getFieldValue('asset').type === 'meeting' ||
                    getFieldValue('asset').type === 'safety_walk' ||
                    getFieldValue('asset').type === 'induction' ||
                    getFieldValue('asset').type === 'toolbox_meeting'
                  ) &&
                    <div>
                      <Row>
                        <Col xs={{ span: 24 }} md={{ span: 24 }}>
                          <Form.Item
                            className="isoInputWrapper"
                            label={<IntlMessages id="healthSafety.history.attendees" />}
                            rules={[
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]}
                          >
                            <Select
                              mode="tags"
                              size="large"
                              allowClear
                              tokenSeparators={[',']}
                              placeholder="Select or type attendees"
                              value={attendees}
                              onChange={handleChangeTag}
                              options={userOptions.map((name) => ({ value: name, label: name }))}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  }
                  {(getFieldValue('asset').type === 'comment' ||
                    getFieldValue('asset').type === 'update' ||
                    getFieldValue('asset').type == 'hazard' ||
                    getFieldValue('asset').type == 'induction' ||
                    getFieldValue('asset').type === 'toolbox_meeting'
                  ) &&
                    <Row>
                      <Col xs={{ span: 24 }} md={{ span: 24 }} >
                        <Form.Item
                          name={['asset', 'comments']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="healthSafety.history.comment" />}
                        >
                          <Input.TextArea />
                        </Form.Item>
                      </Col>
                    </Row>
                  }
                  {(getFieldValue('asset').type === 'comment' ||
                    getFieldValue('asset').type === 'update' ||
                    getFieldValue('asset').type == 'safety_walk' ||
                    getFieldValue('asset').type === 'toolbox_meeting'
                  ) &&
                    <Row>
                      <Col xs={{ span: 24 }} md={{ span: 24 }} >
                        <Form.Item
                          name={['asset', 'attachment']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="propertyPage.modal.asset.attachment" />}
                        >
                          <Upload {...uploadPolicyProps}>
                            <Button icon={<UploadOutlined />}>Upload</Button>
                          </Upload>
                        </Form.Item>
                      </Col>
                    </Row>
                  }
                  {getFieldValue('asset').type == 'safety_walk' &&
                    <Row>
                      <Col xs={{ span: 24 }} md={{ span: 24 }} >
                        <Form.Item
                          name={['asset', 'reporter']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="healthSafety.history.reporter" />}
                        >
                          <Select
                            size="large"
                            allowClear
                            showSearch
                            placeholder="Select reporter"
                            optionFilterProp="label"
                            options={userOptions.map((name) => ({ value: name, label: name }))}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  }
                  {(getFieldValue('asset').type == 'safety_walk' || getFieldValue('asset').type === 'meeting') &&
                    <Row>
                      <Col xs={{ span: 24 }} md={{ span: 24 }} >
                        <Form.Item
                          name={['asset', 'notes']}
                          className="isoInputWrapper"
                          label={<IntlMessages id="healthSafety.history.notes" />}
                        >
                          <Input.TextArea rows={6} placeholder="Enter notes" />
                        </Form.Item>
                      </Col>
                    </Row>
                  }

                  {(getFieldValue('asset').type == 'comment' ||
                    getFieldValue('asset').type == 'hazard') &&
                    <div>
                      <Map ref={mapRef} center={props.position[0] !== null ? props.position : false} doubleClickZoom={false} onclick={handleClickMap} zoom={16} zoomControl={true} style={{ width: '100%', height: '300px' }}>
                        <TileLayer
                          attribution='&copy; Google Maps'
                          url="https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}"
                        />
                        {valueLat && <Marker position={[valueLat, valueLng]} draggable={true}>
                          <Popup position={[valueLat, valueLng]}>
                          </Popup>
                        </Marker>}
                      </Map>
                      <Row style={{ marginTop: "10px", marginBottom: "5px" }}>
                        <Col xs={{ span: 24 }} md={{ span: 11 }} >
                          <Input value={valueLat} />
                        </Col>
                        <Col xs={{ span: 24 }} md={{ span: 11, offset: 2 }} >
                          <Input value={valueLng} />
                        </Col>
                      </Row>
                    </div>
                  }
                </div>
              )
            }
          }}
        </Form.Item>
        <Button key="submit" className="btn-success" type="primary" onClick={handleSubmit} loading={loading}>
          {<IntlMessages id="propertyPage.modal.save" />}
        </Button>
      </div>
    </Form>
  )
}
export default Activity;